import { useState, useMemo, useEffect } from "react";

/**
 * Hook that returns sorted data and helpers to handle column sorting.
 * sortKey: string | null
 * direction: "asc" | "desc"
 *
 * Si pasas `storageKey`, persiste la selección en localStorage por usuario/tabla.
 */
export function useSortable(data, defaultKey = null, defaultDir = "desc", storageKey = null) {
  const [sortKey, setSortKey] = useState(() => {
    if (storageKey) {
      try {
        const raw = localStorage.getItem(`sort:${storageKey}`);
        if (raw) return JSON.parse(raw).key ?? defaultKey;
      } catch {} // eslint-disable-line no-empty
    }
    return defaultKey;
  });
  const [direction, setDirection] = useState(() => {
    if (storageKey) {
      try {
        const raw = localStorage.getItem(`sort:${storageKey}`);
        if (raw) return JSON.parse(raw).dir ?? defaultDir;
      } catch {} // eslint-disable-line no-empty
    }
    return defaultDir;
  });

  useEffect(() => {
    if (!storageKey) return;
    try {
      localStorage.setItem(`sort:${storageKey}`, JSON.stringify({ key: sortKey, dir: direction }));
    } catch {} // eslint-disable-line no-empty
  }, [storageKey, sortKey, direction]);

  const sorted = useMemo(() => {
    if (!sortKey) return data;
    const arr = [...data];
    arr.sort((a, b) => {
      const av = a?.[sortKey];
      const bv = b?.[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") {
        return direction === "asc" ? av - bv : bv - av;
      }
      const as = String(av).toLocaleLowerCase("es");
      const bs = String(bv).toLocaleLowerCase("es");
      if (as < bs) return direction === "asc" ? -1 : 1;
      if (as > bs) return direction === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [data, sortKey, direction]);

  const toggle = (key) => {
    if (sortKey === key) {
      setDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setDirection("desc");
    }
  };

  return { sorted, sortKey, direction, toggle };
}

/** Header cell that toggles sorting on click. */
export function SortableTh({ k, sortKey, direction, toggle, children, className = "", align = "left" }) {
  const active = sortKey === k;
  const arrow = !active ? "↕" : direction === "asc" ? "↑" : "↓";
  return (
    <th
      onClick={() => toggle(k)}
      className={`p-3 font-semibold cursor-pointer select-none hover:text-slate-900 ${className}`}
      style={{ textAlign: align }}
    >
      <span className="inline-flex items-center gap-1.5">
        {children}
        <span className={`text-[10px] ${active ? "text-slate-900" : "text-slate-300"}`}>{arrow}</span>
      </span>
    </th>
  );
}
